import { Stack, ScrollArea } from "@mantine/core"
import { IconEdit, IconPlus } from "@tabler/icons-react"
import {
  ActionIcon,
  AreYouSureModal,
  Button,
  DragEndResult,
  DragNDrop,
  InfoBox,
} from "@tfg/ui"
import { AxiosError } from "axios"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  getProductPageComponentsQueryKey,
  type ProductPageComponentDto,
} from "~/gen/api"
import { useDeleteProductPageComponent } from "~/gen/api/hooks/useDeleteProductPageComponent"
import { useUpdateProductPageComponents } from "~/gen/api/hooks/useUpdateProductPageComponents"
import { useComponentCreation } from "~/pages/Brand/CommunicationStudio/contexts/componentCreation/useComponentCreation"
import { isComponentEditable } from "~/pages/Brand/CommunicationStudio/utils/componentEditability"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const ComponentsEditor = () => {
  const { productSheetGroupId, productPageComponents, openModal } =
    useComponentCreation()
  const { t } = useTranslation("communicationStudio")
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [modalHandlers, setModalHandlers] = useState<{
    handleClose: () => void
    handleConfirm: () => void
  } | null>(null)
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(
    null
  )
  const [optimisticOrder, setOptimisticOrder] = useState<
    { id: string; position: number }[] | null
  >(null)
  const [, setIsOptimisticUpdate] = useState(false)

  const { mutate: updatePageComponents } = useUpdateProductPageComponents({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.setQueryData(
          getProductPageComponentsQueryKey(productSheetGroupId),
          (oldData: { data: typeof productPageComponents } | undefined) => {
            if (!oldData?.data) return oldData

            const orderMap = new Map(
              variables.data.productPageComponents.map((item) => [
                item.productPageComponentId,
                item.order,
              ])
            )

            const updatedData = oldData.data.map((component) => {
              const newOrder = orderMap.get(component.id)
              return newOrder !== undefined
                ? { ...component, order: newOrder }
                : component
            })

            return { ...oldData, data: updatedData }
          }
        )

        setIsOptimisticUpdate(false)
        setOptimisticOrder(null)
        showSuccessNotification({
          title: t("tabs.content.updateProductPageComponents.success.title"),
          message: t(
            "tabs.content.updateProductPageComponents.success.message"
          ),
        })
      },
      onError: (error) => {
        setIsOptimisticUpdate(false)
        setOptimisticOrder(null)
        queryClient.invalidateQueries({
          queryKey: getProductPageComponentsQueryKey(productSheetGroupId),
        })
        showAxiosError({
          error: error as AxiosError,
          showNotification: true,
          notificationMessage: "Failed to update product page components",
        })
      },
    },
  })

  const { mutate: deleteComponent, isPending: isDeletingComponent } =
    useDeleteProductPageComponent({
      mutation: {
        onSuccess: (_data, variables) => {
          queryClient.setQueryData(
            getProductPageComponentsQueryKey(productSheetGroupId),
            (oldData: { data: typeof productPageComponents } | undefined) => {
              if (!oldData?.data) return oldData

              const updatedData = oldData.data.filter(
                (component) => component.id !== variables.id
              )

              return { ...oldData, data: updatedData }
            }
          )

          setIsOptimisticUpdate(false)
          setOptimisticOrder(null)
          queryClient.invalidateQueries({
            queryKey: getProductPageComponentsQueryKey(productSheetGroupId),
          })
          showSuccessNotification({
            title: t(
              "tabs.content.updateProductPageComponents.deleteComponent.success.title"
            ),
            message: t(
              "tabs.content.updateProductPageComponents.deleteComponent.success.message"
            ),
          })
        },
        onError: (error) => {
          showAxiosError({
            error: error as AxiosError,
            showNotification: true,
            notificationMessage: t(
              "tabs.content.updateProductPageComponents.deleteComponent.error.message"
            ),
            notificationTitle: t(
              "tabs.content.updateProductPageComponents.deleteComponent.error.title"
            ),
          })
        },
      },
    })

  const pageComponents = useMemo(() => {
    if (!productPageComponents || productPageComponents.length === 0) {
      return []
    }

    const orderMap = optimisticOrder
      ? new Map(optimisticOrder.map((item) => [item.id, item.position]))
      : null

    return productPageComponents
      .sort((a, b) => {
        const orderA = orderMap?.get(a.id) ?? a.order
        const orderB = orderMap?.get(b.id) ?? b.order
        return orderA - orderB
      })
      .map((component) => {
        const isEditable = isComponentEditable(component.componentType)
        const position = orderMap?.get(component.id) ?? component.order

        return {
          id: component.id,
          label: t(`tabs.content.components.${component.componentType}`),
          position,
          componentType: component.componentType,
          itemProps: {
            bg: "gray.0",
          },
          ...(isEditable && {
            cta: (
              <ActionIcon
                key={component.id}
                icon={<IconEdit />}
                onClick={() => openModal(component, "edit")}
                aria-label="Edit component"
              />
            ),
          }),
        }
      })
  }, [productPageComponents, optimisticOrder, t, openModal])

  const handleDragEnd = (result: DragEndResult) => {
    const { destination, item } = result
    if (!destination) return

    const sourceIndex = pageComponents.findIndex((comp) => comp.id === item.id)
    const destIndex = destination.index
    setIsOptimisticUpdate(true)

    const newOrder = [...pageComponents]
    const [movedItem] = newOrder.splice(sourceIndex, 1)
    newOrder.splice(destIndex, 0, movedItem)
    const updatedOrder = newOrder.map((component, index) => ({
      id: component.id,
      position: index + 1,
    }))

    setOptimisticOrder(updatedOrder)
    updatePageComponents({
      data: {
        productPageComponents: newOrder.map(
          (component, index) =>
            ({
              productPageComponentId: component.id,
              order: updatedOrder[index].position,
              componentType: component.componentType,
            }) satisfies ProductPageComponentDto
        ),
      },
    })
  }

  const confirmDelete = (id: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setSelectedComponentId(id)
      setIsConfirmModalOpen(true)

      const handleClose = () => {
        setIsConfirmModalOpen(false)
        resolve(false)
      }

      const handleConfirm = () => {
        setIsConfirmModalOpen(false)
        resolve(true)
      }
      setModalHandlers({ handleClose, handleConfirm })
    })
  }

  return (
    <>
      {pageComponents && pageComponents.length > 0 ? (
        <Stack
          w="100%"
          align="flex-start"
          pos="relative"
          justify="flex-start"
          style={{ overflow: "hidden" }}
        >
          <ScrollArea h="60vh" w="100%" p="sm">
            <DragNDrop
              data={pageComponents}
              onDragEnd={handleDragEnd}
              showPosition
              onDelete={(id) => deleteComponent({ id })}
              beforeDelete={(id) => confirmDelete(id)}
              deletable={true}
            />
          </ScrollArea>
          <Button
            variant="tertiary"
            color="primary"
            icon={<IconPlus size={16} />}
            onClick={() => openModal(null, "create")}
            disabled={isDeletingComponent}
          >
            {t("tabs.content.componentsEditor.createComponent")}
          </Button>
          <AreYouSureModal
            opened={isConfirmModalOpen}
            onClose={() => modalHandlers?.handleClose()}
            onConfirm={() => {
              if (!selectedComponentId) return
              modalHandlers?.handleConfirm()
            }}
            areYouSureText={t(
              "tabs.content.updateProductPageComponents.deleteComponent.areYouSureText"
            )}
            confirmText={t(
              "tabs.content.updateProductPageComponents.deleteComponent.confirmText"
            )}
            cancelText={t(
              "tabs.content.updateProductPageComponents.deleteComponent.cancelText"
            )}
          />
        </Stack>
      ) : (
        <InfoBox
          title="No components found"
          message="Please add a component to the product sheet"
        />
      )}
    </>
  )
}
